import { ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material";

import { SearchField } from "ui/search-field/search-field";
import { AppliedFilters } from "ui/data-grid/components/applied-filters/applied-filters";
import { useAppliedFilters } from "ui/data-grid/components/applied-filters/use-applied-filters";
import type {
  AppliedFiltersState,
  NoAppliedFiltersState,
} from "ui/data-grid/components/applied-filters/applied-filters.types";

import type { FiltersToApply } from "types/filters";

const SEARCH_FIELD_SX: SxProps<Theme> = { width: 512 };

type BaseProps = {
  tableFilters?: ReactNode;
  setCurrentSearchTerm: (term: string) => void;
  placeholder?: string;
  /** The search field's root. Defaults to the 512px every table search bar is drawn at. */
  searchSx?: SxProps<Theme>;
  actionSlot?: ReactNode;
};

type TableSearchBarProps<TFilters extends FiltersToApply> = BaseProps &
  (AppliedFiltersState<TFilters> | NoAppliedFiltersState);

export const TableSearchAndFilters = <TFilters extends FiltersToApply>({
  tableFilters,
  setCurrentSearchTerm,
  filtersToApply,
  setFiltersToApply,
  defaultFiltersToApply,
  filterIdToTitle,
  filterValueToLabel,
  placeholder,
  searchSx = SEARCH_FIELD_SX,
  actionSlot,
}: TableSearchBarProps<TFilters>) => {
  const { items, handleRemove, handleClearAll } = useAppliedFilters({
    filtersToApply,
    setFiltersToApply,
    defaultFiltersToApply,
    filterIdToTitle,
    filterValueToLabel,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <SearchField onChange={setCurrentSearchTerm} placeholder={placeholder} sx={searchSx} />
          {tableFilters}
        </div>
        {actionSlot}
      </div>

      {items.length > 0 && (
        <AppliedFilters items={items} onRemove={handleRemove} onClearAll={handleClearAll} />
      )}
    </div>
  );
};
